"use client";

import { useState } from "react";
import { Checkbox, SelectField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { ArithmeticSheet } from "@/components/tools/worksheets-math/arithmetic-sheet";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import {
  CommonControls,
  defaultSettings,
  UnmetNotice,
  useCommonSettings,
  useWorksheetSeed,
} from "@/components/tools/worksheets-math/worksheet-kit";
import {
  type AdditionOptions,
  generateAddition,
  type MissingMode,
  type RegroupMode,
} from "@/lib/tool-kit/worksheets/arithmetic";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const ADDITION_WORKSHEET_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Addition Practice",
  presetsLabel: "Quick levels",
  presets: {
    facts10: "Sums within 10",
    facts20: "Single-digit facts",
    twoNoRegroup: "2-digit, no regrouping",
    twoRegroup: "2-digit, regrouping",
    threeDigit: "3-digit",
    threeAddends: "Three 2-digit addends",
  },
  addendsLabel: "Numbers to add",
  firstDigitsLabel: "Digits in the first number",
  otherDigitsLabel: "Digits in the other numbers",
  regroupLabel: "Regrouping (carrying)",
  regroups: {
    any: "Allowed, not required",
    none: "None — no column carries",
    always: "Required — at least one carry",
  },
  maxSumLabel: "Largest sum",
  maxSumNone: "No limit",
  allowZero: "Include 0 as an addend",
  unknownLabel: "What the student finds",
  unknowns: {
    answer: "The sum (4 + 3 = ?)",
    missing: "A missing addend (4 + ? = 7)",
    mixed: "A mix of both",
  },
  unknownHint:
    "Missing-addend problems use the one-line layout and two addends.",
  carryBoxes: "Carry boxes above the columns",
} as const;

type Fields = Omit<AdditionOptions, "count" | "missing">;

const START: Fields = {
  addends: 2,
  firstDigits: 2,
  otherDigits: 2,
  regroup: "any",
  maxSum: 0,
  allowZero: false,
};

const PRESETS: Record<
  keyof typeof ADDITION_WORKSHEET_GENERATOR_STRINGS.presets,
  Partial<Fields>
> = {
  facts10: {
    addends: 2,
    firstDigits: 1,
    otherDigits: 1,
    regroup: "any",
    maxSum: 10,
    allowZero: true,
  },
  facts20: {
    addends: 2,
    firstDigits: 1,
    otherDigits: 1,
    regroup: "any",
    maxSum: 0,
    allowZero: false,
  },
  twoNoRegroup: {
    addends: 2,
    firstDigits: 2,
    otherDigits: 2,
    regroup: "none",
    maxSum: 0,
    allowZero: false,
  },
  twoRegroup: {
    addends: 2,
    firstDigits: 2,
    otherDigits: 2,
    regroup: "always",
    maxSum: 0,
    allowZero: false,
  },
  threeDigit: {
    addends: 2,
    firstDigits: 3,
    otherDigits: 3,
    regroup: "any",
    maxSum: 0,
    allowZero: false,
  },
  threeAddends: {
    addends: 3,
    firstDigits: 2,
    otherDigits: 2,
    regroup: "any",
    maxSum: 0,
    allowZero: false,
  },
};

const MAX_SUM_CHOICES = ["0", "10", "20", "100", "1000"];
const digitChoices = (max: number) =>
  Array.from({ length: max }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));

export default function AdditionWorksheetGeneratorWidget() {
  const strings = useToolUi(ADDITION_WORKSHEET_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(defaultSettings({ count: 20 }));
  const seed = useWorksheetSeed();
  const [fields, setFields] = useState<Fields>(START);
  const [missing, setMissing] = useState<MissingMode>("answer");
  const [carryBoxes, setCarryBoxes] = useState(false);

  const patch = (next: Partial<Fields>) =>
    setFields((current) => ({ ...current, ...next }));
  const oneLine = settings.layout === "horizontal";
  const generated = generateAddition(
    {
      ...fields,
      count: settings.count,
      missing: oneLine && fields.addends === 2 ? missing : "answer",
    },
    createRng(seed.seed)
  );
  const longest = Math.max(fields.firstDigits, fields.otherDigits);
  const boxes = carryBoxes && !oneLine ? Math.max(0, longest - 1) : 0;

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="space-y-2">
          <p className={LABEL}>{strings.presetsLabel}</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((key) => (
              <button
                className={BUTTON_SMALL}
                key={key}
                onClick={() => patch({ ...START, ...PRESETS[key] })}
                type="button"
              >
                {strings.presets[key]}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="aw-addends"
            label={strings.addendsLabel}
            onChange={(value) => patch({ addends: Number(value) })}
            options={[
              { value: "2", label: "2" },
              { value: "3", label: "3" },
              { value: "4", label: "4" },
            ]}
            value={String(fields.addends)}
          />
          <SelectField
            id="aw-first"
            label={strings.firstDigitsLabel}
            onChange={(value) => patch({ firstDigits: Number(value) })}
            options={digitChoices(5)}
            value={String(fields.firstDigits)}
          />
          <SelectField
            id="aw-other"
            label={strings.otherDigitsLabel}
            onChange={(value) => patch({ otherDigits: Number(value) })}
            options={digitChoices(5)}
            value={String(fields.otherDigits)}
          />
          <SelectField
            id="aw-max-sum"
            label={strings.maxSumLabel}
            onChange={(value) => patch({ maxSum: Number(value) })}
            options={MAX_SUM_CHOICES.map((value) => ({
              value,
              label: value === "0" ? strings.maxSumNone : value,
            }))}
            value={String(fields.maxSum)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="aw-regroup"
            label={strings.regroupLabel}
            onChange={(regroup: RegroupMode) => patch({ regroup })}
            options={(["any", "none", "always"] as const).map((value) => ({
              value,
              label: strings.regroups[value],
            }))}
            value={fields.regroup}
          />
          <SelectField
            hint={oneLine ? undefined : strings.unknownHint}
            id="aw-unknown"
            label={strings.unknownLabel}
            onChange={setMissing}
            options={(["answer", "missing", "mixed"] as const).map((value) => ({
              value,
              label: strings.unknowns[value],
            }))}
            value={oneLine ? missing : "answer"}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={fields.allowZero}
            label={strings.allowZero}
            onChange={(allowZero) => patch({ allowZero })}
          />
          <Checkbox
            checked={carryBoxes}
            label={strings.carryBoxes}
            onChange={setCarryBoxes}
          />
        </div>
        <CommonControls
          idPrefix="aw"
          onChange={update}
          seed={seed}
          settings={settings}
          strings={strings}
        />
        <UnmetNotice strings={strings} unmet={generated.unmet} />
      </div>
      <ArithmeticSheet
        problems={generated.problems}
        scaffoldBoxes={boxes}
        seed={seed.seed}
        settings={settings}
        strings={strings}
        title={settings.title.trim() || strings.defaultTitle}
        workRows={0}
      />
    </div>
  );
}
